import { db, doc, updateDoc, serverTimestamp, arrayUnion } from '../firebase/config';
import { createNotification } from './notifications';

/**
 * Reminder windows in hours.
 * The first time a booking enters one of these windows,
 * we fire a notification.
 */
const REMINDER_WINDOWS = [
  { key: 'twoDays', hours: 48, label: 'in 2 days' },
  { key: 'twoHours', hours: 2, label: 'in 2 hours' },
  { key: 'atStart', hours: 0, label: 'now' },
];

/**
 * Check a list of bookings and fire any due reminders.
 * Requires browser notification permission for full effect.
 *
 * @param {Array} bookings - client's bookings (already loaded)
 * @param {string} userId - the current user's uid
 * @returns {Array} - list of fired reminder objects
 */
export const checkAndFireReminders = async (bookings, userId) => {
  if (!userId || !Array.isArray(bookings)) return [];

  const now = Date.now();
  const fired = [];

  for (const booking of bookings) {
    // Only for confirmed, upcoming bookings
    if (booking.status !== 'confirmed') continue;
    if (!booking.date) continue;

    // Skip if already has an active reminder or was dismissed
    const sentReminders = booking.remindersSent || {};

    const appointmentTime = new Date(
      `${booking.date}T${booking.time || '00:00'}`
    ).getTime();
    if (Number.isNaN(appointmentTime)) continue;
    if (appointmentTime < now) continue; // already past

    const hoursUntil = (appointmentTime - now) / (1000 * 60 * 60);

    for (const window of REMINDER_WINDOWS) {
      // Skip if already sent this reminder
      if (sentReminders[window.key]) continue;

      // Fire this reminder if we're within its window
      // (and not past a later window)
      const shouldFire =
        (window.key === 'twoDays' && hoursUntil <= 48 && hoursUntil > 2) ||
        (window.key === 'twoHours' && hoursUntil <= 2 && hoursUntil > 0.083) ||
        (window.key === 'atStart' && hoursUntil <= 0.083);

      if (!shouldFire) continue;

      // 1. Create the notification doc
      const notifId = await createNotification({
        userId,
        type: 'booking_reminder',
        title: booking.serviceTitle || booking.serviceName || 'Booking reminder',
        body: `Your appointment with ${booking.professionalName || 'the professional'} is ${window.label}`,
        link: `/dashboard/calendar`,
        metadata: {
          bookingId: booking.id,
          window: window.key,
        },
      });

      // 2. Mark this reminder as sent on the booking
      try {
        await updateDoc(doc(db, 'bookings', booking.id), {
          [`remindersSent.${window.key}`]: true,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error('Failed to mark reminder sent:', err);
      }

      // 3. Fire a browser notification if we have permission
      fireBrowserNotification(
        booking.serviceTitle || booking.serviceName || 'Booking reminder',
        `With ${booking.professionalName || 'Professional'} ${window.label}`
      );

      fired.push({
        bookingId: booking.id,
        window: window.key,
        notifId,
      });

      // Only fire one reminder per booking per check
      break;
    }
  }

  return fired;
};

/**
 * Fire a browser push notification (if permission granted).
 */
export const fireBrowserNotification = (title, body) => {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
    });
  } catch (err) {
    console.warn('Browser notification failed:', err);
  }
};

/**
 * Ask the browser for notification permission.
 * Returns the resulting permission status.
 */
export const requestNotificationPermission = async () => {
  if (typeof window === 'undefined') return 'denied';
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';

  try {
    const result = await Notification.requestPermission();
    return result;
  } catch (err) {
    console.error('Notification permission failed:', err);
    return 'denied';
  }
};